import { de, enGB, enUS } from 'date-fns/locale';
import { getLocale } from './locale';
import {
  addMilliseconds,
  addMinutes,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInYears,
  format,
  startOfToday,
} from 'date-fns';

export const StartDateForTimeOnlyType = startOfToday();
//addMinutes(startOfToday(), startOfToday().getTimezoneOffset());
export const TimeWithSecondsFormat = 'pp';

export function localFormat(date: Date | number, dateFormat: string): string {
  return format(date, dateFormat, { locale: getLocale() });
}

export function getDateFormat(): string {
  switch (getLocale()) {
    case enUS:
      return 'M/d/yyyy';
    case de:
      return 'dd.MM.yyyy';
    case enGB:
    default:
      return 'dd/MM/yyyy';
  }
}

export function roundUsingFormat(value: string, format: string): string {
  if (format === '0') {
    return Math.round(Number.parseFloat(value)).toString();
  }
  const checkedFormat = format.match(/^0.0+/);
  if (checkedFormat) {
    const num = Number.parseFloat(value);
    const pow = checkedFormat[0].length - 2;
    const factor = Math.pow(10, pow);
    const result = Math.round(num * factor) / factor;
    return result.toString();
  }
  return value;
}

/*
 * We accept the following dateFormat:
 * - 'h.0', 'm.0', 's.0'; there could be more zeroes
 * - any of the 'y,M,d, h:m:s, hh:mm:ss' (but here you can't use `.00`)
 */
export function localFormatDuration(
  totalMilliseconds: number | Date,
  dateFormat: string
): string {
  if (totalMilliseconds === undefined || totalMilliseconds === null) return '';

  const startDate = StartDateForTimeOnlyType;
  const endDate =
    totalMilliseconds instanceof Date
      ? totalMilliseconds
      : addMilliseconds(startDate, totalMilliseconds);
  if (isSimpleTimeFormat(dateFormat)) {
    return simpleFormatTimeAsNumber(endDate, dateFormat);
  }
  const tokens = ['y', 'M', 'd', 'h', 'm', 's'];
  const values: Record<string, string> = {};

  let aggregate = 0;
  for (const token of tokens) {
    if (token === 'y') {
      const diff = differenceInYears(endDate, startDate);
      values[token] = diff.toString();
      if (dateFormat.includes(token)) {
        aggregate = aggregate * 12 + diff * 12;
      }
    }
    if (token === 'M') {
      const diff = differenceInMonths(endDate, startDate) - aggregate;
      values[token] = diff.toString();
      if (dateFormat.includes(token)) {
        aggregate = aggregate * 12 + diff * 12;
      }
    }
    if (token === 'd') {
      const diff = differenceInDays(endDate, startDate);
      values[token] = diff.toString();
      if (dateFormat.includes(token)) {
        aggregate = diff * 24;
      }
    }
    if (token === 'h') {
      const diff = differenceInHours(endDate, startDate) - aggregate;
      values[token] = diff.toString();
      if (dateFormat.includes(token)) {
        aggregate = diff * 60 + aggregate * 60;
      }
    }
    if (token === 'm') {
      const diff = differenceInMinutes(endDate, startDate) - aggregate;
      values[token] = diff.toString();
      if (dateFormat.includes(token)) {
        aggregate = diff * 60 + aggregate * 60;
      }
    }
    if (token === 's') {
      const diff = differenceInSeconds(endDate, startDate) - aggregate;
      values[token] = diff.toString();
      if (dateFormat.includes(token)) {
        aggregate = diff * 60 + aggregate * 60;
      }
    }
  }
  let result = dateFormat;
  tokens.unshift('ss');
  tokens.unshift('mm');
  tokens.unshift('hh');
  values['ss'] = values['s'].toString().padStart(2, '0');
  values['mm'] = values['m'].toString().padStart(2, '0');
  values['hh'] = values['h'].toString().padStart(2, '0');
  for (const token of tokens) {
    result = result.replace(token, values[token]?.toString());
  }
  return result;
}

/*
  format: we accept the following values: 'h', 'h.0', 'm', 'm.0', 's', 's.0'; there could be more zeroes.
  If you add more formats, make sure to adjust `isTimeFormatValid` and `convertStringToTime` functions below.
*/
export function simpleFormatTimeAsNumber(
  value: Date | undefined,
  format: string
): string {
  if (value === undefined || value === null) return '';
  if (format === TimeWithSecondsFormat || format === 'p') {
    return (
      differenceInHours(value, StartDateForTimeOnlyType) +
      localFormat(value, ':mm:ss')
    );
  }

  const timeInSeconds =
    (value.getTime() - StartDateForTimeOnlyType.getTime()) / 1000;

  const factor = getFactor(format);
  const numberFormat = '0' + format.substr(1); // numberFormat should be something like '0.0' or '0.00', etc
  return roundUsingFormat((timeInSeconds / factor).toString(), numberFormat);
}

export function isSimpleTimeFormat(format: string) {
  if (format === TimeWithSecondsFormat || format === 'p') return true;
  return format.match(/^(h|m|s)$/) || format.match(/^(h|m|s).0+$/);
}

export function getFactor(format: string): number {
  switch (format.charAt(0)) {
    case 's':
      return 1;
    case 'm':
      return 60;
    case 'h':
      return 3600;
    default:
      throw new Error(`Unknown format '${format}' for parsing`);
  }
}
